import { useState } from 'react';
import { CheckCircle } from 'lucide-react';
import { toast } from 'sonner';
import { subscribeSchema } from '@auditwire/contracts';
import { toMessage } from '@/shared/lib/repository/errors';
import { useSubscribe } from '../api/useNewsletter';

export interface NewsletterBandProps {
  eyebrow?: string;
  title: string;
  subtitle?: string;
}

/** Dark newsletter call-to-action band wired to the double opt-in subscribe RPC. */
export function NewsletterBand({ eyebrow = 'Every Thursday · Free', title, subtitle }: NewsletterBandProps) {
  const [email, setEmail] = useState('');
  const [done, setDone] = useState<'pending' | 'confirmed' | null>(null);
  const subscribe = useSubscribe();

  const submit = async () => {
    const parsed = subscribeSchema.safeParse({ email });
    if (!parsed.success) {
      toast.error(parsed.error.issues[0]?.message ?? 'Enter a valid email');
      return;
    }
    try {
      const status = await subscribe.mutateAsync(parsed.data.email);
      // 'unsubscribed' can't come back from subscribe_email (it reopens as pending).
      setDone(status === 'confirmed' ? 'confirmed' : 'pending');
    } catch (err) {
      toast.error(toMessage(err, 'Could not subscribe you just now'));
    }
  };

  return (
    <section className="relative overflow-hidden rounded-3xl bg-zinc-950 p-8">
      <div
        className="pointer-events-none absolute inset-0"
        style={{
          background:
            'radial-gradient(ellipse 80% 80% at 100% 100%, rgba(251,191,36,0.08) 0%, transparent 60%)',
        }}
      />
      <div className="relative z-10">
        <p className="mb-2 font-mono text-[10px] font-bold uppercase tracking-widest text-amber-400">
          {eyebrow}
        </p>
        <h3 className="mb-2 font-display text-2xl font-bold text-white">{title}</h3>
        {subtitle ? <p className="mb-5 text-sm leading-relaxed text-zinc-400">{subtitle}</p> : null}
        {done ? (
          <div className="flex items-start gap-2 text-emerald-400">
            <CheckCircle className="mt-0.5 h-4 w-4 shrink-0" />
            <span className="text-sm font-medium">
              {done === 'confirmed'
                ? 'You’re already subscribed — see you Thursday.'
                : 'Almost there: check your inbox to confirm.'}
            </span>
          </div>
        ) : (
          <div className="flex gap-2">
            <label className="sr-only" htmlFor="newsletter-email">
              Email address
            </label>
            <input
              id="newsletter-email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="your@email.com"
              className="min-w-0 flex-1 rounded-full border border-white/10 bg-white/10 px-4 py-2.5 text-sm text-white placeholder:text-white/30 focus:outline-none focus:ring-2 focus:ring-amber-400/50"
            />
            <button
              type="button"
              onClick={() => void submit()}
              disabled={subscribe.isPending}
              className="shrink-0 rounded-full bg-amber-400 px-5 py-2.5 text-sm font-bold text-zinc-950 transition-all hover:bg-amber-300 active:scale-95 disabled:opacity-50"
            >
              {subscribe.isPending ? 'Subscribing…' : 'Subscribe'}
            </button>
          </div>
        )}
      </div>
    </section>
  );
}
